import type { TFunction } from "i18next";

import {
  REGIONAL_CATALOG_TEMPLATE,
  REGIONAL_NORMS_BY_STATE,
} from "./regional-classification.catalog-template";
import {
  REGIONAL_STATES,
  type ClassificationLevel,
  type RegionalNorm,
  type RegionalNormBands,
  type RegionalNormDef,
  type RegionalState,
  type SectionDef,
} from "./regional-classification.catalog.types";

const CATALOG_PREFIX = "admin.structures.regionalClassification.catalog";

const catalogKey = (relativeKey: string) => `${CATALOG_PREFIX}.${relativeKey}`;

const buildSections = (
  translate: TFunction,
  norm: RegionalNorm,
): SectionDef[] =>
  REGIONAL_CATALOG_TEMPLATE[norm].sections.map((section) => ({
    key: section.key,
    label: translate(catalogKey(section.labelKey)),
    subtitle: translate(catalogKey(section.subtitleKey)),
    hasBignumber: section.hasBignumber,
    grades: section.grades.map((grade) => ({
      key: grade.key,
      code: grade.code,
      fullTitle: translate(catalogKey(grade.fullTitleKey)),
      columns: grade.columns.map((column) => ({
        key: column.key,
        letter: column.letter,
        title: translate(catalogKey(column.titleKey)),
        options: column.options.map((option) => ({
          score: option.score,
          text: translate(catalogKey(option.textKey)),
        })),
      })),
    })),
  }));

/** UFs ofertadas no select de Estado (passo 1 do fluxo). v1: somente MG. */
export const getRegionalStates = (): RegionalState[] => [...REGIONAL_STATES];

/**
 * Normas de um estado — fonte do radio APÓS a escolha da UF (passo 2 do fluxo).
 * Catálogo de referência (código), não a tabela de instância de classificação.
 */
export const getRegionalNormsForState = (state: RegionalState): RegionalNorm[] =>
  REGIONAL_NORMS_BY_STATE[state] ?? [];

/** Constrói a norma regional já traduzida (label + seções + faixas). */
export const buildRegionalNorm = (
  translate: TFunction,
  norm: RegionalNorm,
): RegionalNormDef => {
  const template = REGIONAL_CATALOG_TEMPLATE[norm];
  return {
    norm,
    state: template.state,
    label: translate(catalogKey(template.labelKey)),
    secondSectionKey: template.secondSectionKey,
    bands: template.bands,
    sections: buildSections(translate, norm),
  };
};

/** Chaves planas de todas as colunas da norma (CRI + 2ª seção). */
export const getAllRegionalColumnKeys = (norm: RegionalNorm): string[] =>
  REGIONAL_CATALOG_TEMPLATE[norm].sections.flatMap((section) =>
    section.grades.flatMap((grade) => grade.columns.map((column) => column.key)),
  );

/** Chaves das colunas EC (usadas pela regra especial EC* → CRI Alto). */
export const getRegionalEcColumnKeys = (norm: RegionalNorm): string[] =>
  REGIONAL_CATALOG_TEMPLATE[norm].sections
    .filter((section) => section.key === "cri")
    .flatMap((section) =>
      section.grades
        .filter((grade) => grade.key === "ec")
        .flatMap((grade) => grade.columns.map((column) => column.key)),
    );

/**
 * Faixa CRI: Alto se total >= highMin OU alguma coluna EC >= ecHighScore
 * (regra especial EC*; usa >= porque colunas EC com teto maior — ex.: água/IGAM
 * com coluna EC de teto 10 e gatilho 8 — também disparam Alto); Médio se total >
 * mediumMin; senão Baixo.
 */
export const classifyCri = (
  total: number,
  ecScores: number[],
  bands: RegionalNormBands,
): ClassificationLevel => {
  if (total >= bands.cri.highMin || ecScores.some((s) => s >= bands.cri.ecHighScore)) {
    return "high";
  }
  if (total > bands.cri.mediumMin) {
    return "medium";
  }
  return "low";
};

/** Faixa da 2ª seção (PDA/DPA): Alto >= highMin; Médio > mediumMin; senão Baixo. */
export const classifySecondSection = (
  total: number,
  bands: RegionalNormBands,
): ClassificationLevel => {
  if (total >= bands.second.highMin) {
    return "high";
  }
  if (total > bands.second.mediumMin) {
    return "medium";
  }
  return "low";
};
